// Cloudinary inline transform helper.
//
// We seed avatars to Cloudinary without size/format presets, so the original
// image (often 200KB-2MB) gets returned every time. Slow on Telegram WebView
// over China-→-Cloudinary CDN. This helper rewrites the URL to add
// `c_fill,f_auto,q_auto,w_W[,h_H]` so the CDN returns a small,
// well-compressed version per use case.
//
// Usage:
//   cdn(url, "thumb")   // ~64x64 list avatars
//   cdn(url, "card")    // ~256x256 contact grid
//   cdn(url, "hero")    // ~720x900 profile hero
//
// No-ops for non-Cloudinary URLs (Telegram avatar CDN, base64, etc.).

const PRESETS = {
  thumb: "c_fill,g_face,f_auto,q_auto,w_128,h_128,dpr_2.0",
  card: "c_fill,g_face,f_auto,q_auto,w_256,h_256,dpr_2.0",
  // hero shrunk for faster load. dpr=1 (not 1.5) on a ~360px-wide phone the
  // physical pixels are ≤720, so 480 logical w renders fine and halves bytes.
  hero: "c_fill,g_face,f_auto,q_auto:eco,w_480,h_640,dpr_1.0",
  // Tiny blurred placeholder shown immediately while hero loads.
  // Cloudinary returns a 32px-wide JPEG with heavy blur ≈ 800–1500 bytes.
  blur: "c_fill,g_face,w_32,h_42,e_blur:800,q_30,f_auto",
  // Chat bubble — small inline preview, fits the 220px max-width bubble.
  chatPreview: "c_limit,f_auto,q_auto:eco,w_400,dpr_2.0",
  // Tap-to-zoom view of a chat image — readable but not original-sized.
  chatFull: "c_limit,f_auto,q_auto,w_1280",
  full: "f_auto,q_auto,w_1280",
};

export function cdn(url, preset = "thumb") {
  if (!url || typeof url !== "string") return url;
  // Cloudinary delivery URLs include "/image/upload/". Anything else
  // (Telegram CDN, data URIs, base64, http file servers) we leave alone.
  const marker = "/image/upload/";
  const i = url.indexOf(marker);
  if (i < 0) return url;
  const t = PRESETS[preset] || PRESETS.thumb;
  // Don't double-apply if a transform string is already there. Cheap detection:
  // the segment immediately after /upload/ contains commas in our presets.
  const afterMarker = url.slice(i + marker.length);
  const firstSeg = afterMarker.split("/")[0] || "";
  if (firstSeg.includes(",") || firstSeg.startsWith("c_") || firstSeg.startsWith("w_")) {
    return url; // already transformed
  }
  return url.slice(0, i + marker.length) + t + "/" + afterMarker;
}
